import { t } from "@lingui/macro";
import { useCallback, useMemo, useState, useEffect } from "react";

import { RangeSlider } from "@whoosh/design-system/src/components/controls/RangeSlider";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  formatSliderTimeLabel,
  getSliderOptions,
  normalizeSliderTimeValue,
} from "../../utils/getSliderOptions";

type Props = {
  controlStartTime?: string;
  controlEndTime?: string;
  selectedTimeRange?: { timeGTE?: string; timeLTE?: string };
  onChange: (value: { timeGTE?: string; timeLTE?: string }) => void;
};
export const TimeOfDayFilterControl = ({
  controlStartTime,
  controlEndTime,
  selectedTimeRange,
  onChange,
}: Props) => {
  const sliderOptions = useMemo(
    () => getSliderOptions(controlStartTime, controlEndTime),
    [controlStartTime, controlEndTime]
  );
  const normalizedSelectedTimeGTE = normalizeSliderTimeValue(
    selectedTimeRange?.timeGTE
  );
  const normalizedSelectedTimeLTE = normalizeSliderTimeValue(
    selectedTimeRange?.timeLTE
  );
  const normalizedControlStartTime = normalizeSliderTimeValue(controlStartTime);
  const normalizedControlEndTime = normalizeSliderTimeValue(controlEndTime);

  const initialLeftPercent = useMemo(() => {
    return (
      sliderOptions.find((option) => option.value === normalizedSelectedTimeGTE)
        ?.percentage ?? 0
    );
  }, [normalizedSelectedTimeGTE, sliderOptions]);

  const initialRightPercent = useMemo(() => {
    return (
      sliderOptions.find((option) => option.value === normalizedSelectedTimeLTE)
        ?.percentage ?? 100
    );
  }, [normalizedSelectedTimeLTE, sliderOptions]);

  useEffect(() => {
    setLeftKnobPercent(initialLeftPercent);
    setRightKnobPercent(initialRightPercent);
  }, [initialLeftPercent, initialRightPercent]);

  const selectedTimeRangeDisplay = useMemo(() => {
    if (
      !selectedTimeRange ||
      (normalizedSelectedTimeGTE === normalizedControlStartTime &&
        normalizedSelectedTimeLTE === normalizedControlEndTime)
    ) {
      return <Text variant="mainBaseMedium">{t`All Day`}</Text>;
    }
    const startTime = selectedTimeRange.timeGTE
      ? formatSliderTimeLabel(selectedTimeRange.timeGTE)
      : null;
    const endTime = selectedTimeRange.timeLTE
      ? formatSliderTimeLabel(selectedTimeRange.timeLTE)
      : null;
    return (
      <Text variant="mainBaseMedium" color="text-success">
        {startTime} - {endTime}
      </Text>
    );
  }, [
    normalizedControlEndTime,
    normalizedControlStartTime,
    normalizedSelectedTimeGTE,
    normalizedSelectedTimeLTE,
    selectedTimeRange,
  ]);

  const [leftKnobPercent, setLeftKnobPercent] = useState(initialLeftPercent);
  const [rightKnobPercent, setRightKnobPercent] = useState(initialRightPercent);

  const onSliderChange = useCallback(
    (
      steppedLeftTime: string,
      steppedRightTime: string,
      rawLeftPercent: number,
      rawRightPercent: number
    ) => {
      if (
        steppedLeftTime === controlStartTime &&
        steppedRightTime === controlEndTime
      ) {
        onChange({ timeGTE: controlStartTime, timeLTE: controlEndTime });
      } else {
        onChange({ timeGTE: steppedLeftTime, timeLTE: steppedRightTime });
      }
      setLeftKnobPercent(rawLeftPercent);
      setRightKnobPercent(rawRightPercent);
    },
    [controlStartTime, controlEndTime, onChange]
  );

  return (
    <VStack gap={6} flex={1}>
      <HStack justifyContent="space-between">
        <Text variant="mainSmallSemiBold">{t`Time of Day`}</Text>
        {selectedTimeRangeDisplay}
      </HStack>

      {sliderOptions.length > 0 ? (
        <Box px={4}>
          <RangeSlider
            currentLeftPercent={leftKnobPercent}
            currentRightPercent={rightKnobPercent}
            percentValuesMap={sliderOptions}
            onChange={onSliderChange}
          />
        </Box>
      ) : null}
    </VStack>
  );
};
